<template>
  <SlideIn :open="isOpen" heading="Stock Notification" @close="isOpen = false">
    <template #content>
      <form v-if="product" ref="form" class="form">
        <p class="form__caption">
          Email me when the "{{ product.title }}" comes back in stock in
          {{ variant.title }}.
        </p>

        <TextField
          :value="email"
          :validation="v$.email"
          type="email"
          autocomplete="email"
          label="E-mail address"
          @input="(value) => (email = value)"
        />

        <template v-if="isSMSStockNotificationEnabled">
          <label class="field field--checkbox">
            <span class="field__label">Send me a text as well</span>
            <div class="input input--checkbox">
              <input
                v-model="showPhoneNumberInput"
                type="checkbox"
                class="input__element input__element--checkbox"
              />
              <span class="input__checkbox"></span>
            </div>
          </label>

          <template v-if="showPhoneNumberInput">
            <div class="telephone-wrapper">
              <label class="field">
                <div class="input">
                  <select
                    class="input__element"
                    :value="usersCountryCode"
                    @change="handleCodeChange"
                  >
                    <option
                      v-for="code in telephoneCodesOrdered"
                      :key="code.code"
                      :value="code.code"
                    >
                      {{ code.dial_code }}
                    </option>
                  </select>
                  <div
                    v-if="v$.selectedDialCode.$dirty"
                    class="input__response"
                  >
                    <ul>
                      <li
                        v-for="error of v$.selectedDialCode.$errors"
                        :key="error.$uid"
                      >
                        {{ error.$message }}
                      </li>
                    </ul>
                  </div>
                </div>
              </label>

              <TextField
                :value="telephone"
                :validation="v$.telephone"
                type="tel"
                autocomplete="tel"
                label="Phone number"
                @input="(value) => (telephone = value)"
              />
            </div>
            <div class="legal">
              By signing up via text, you agree to receive recurring automated
              marketing messages, including cart reminders, at the phone number
              provided. Consent is not a condition of purchase. Reply STOP to
              unsubscribe. Reply HELP for help.
            </div>
          </template>

          <label class="field field--checkbox">
            <span class="field__label">Recieve offers and promotions</span>
            <div class="input input--checkbox">
              <input
                v-model="marketing"
                type="checkbox"
                class="input__element input__element--checkbox"
              />
              <span class="input__checkbox"></span>
            </div>
          </label>
        </template>
      </form>

      <template v-if="submitted">
        <div v-if="success" class="input form__response">
          <div class="input__response input__response--success">
            Success! You'll be notified when the product comes back in stock.
          </div>
        </div>
        <div v-if="!success" class="input form__response">
          <div class="input__response input__response--failure">
            Something went wrong, please try again later.
          </div>
        </div>
      </template>
    </template>
    <template #footer>
      <Button type="button" full-width @click="handleFormSubmit">
        Notify me when back in stock
      </Button>
    </template>
  </SlideIn>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { email, numeric, required } from "@vuelidate/validators";
import cloneDeep from "lodash-es/cloneDeep";
import { mapGetters, mapState } from "vuex";

import telephoneCodes from "@/assets/json/telephone-codes.json";
import Button from "@/components/Button.vue";
import SlideIn from "@/components/SlideIn.vue";
import { klaviyoLists } from "@/config";
import { regionFeatureToggles } from "@/config.js";
import { smsCaptureEvent, bisEvent } from "@/helpers/events.js";
import TextField from "@/components/TextField.vue";

export default {
  name: "StockNotifications",
  components: {
    Button,
    SlideIn,
    TextField,
  },
  props: {
    variant: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:open"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      open: false,
      email: "",
      marketing: true,
      success: false,
      showPhoneNumberInput: false,
      telephone: null,
      submitted: false,
      codes: telephoneCodes,
      selectedDialCode: null,
      usersCountryCode: null,
    };
  },
  computed: {
    ...mapState("add-to-bag", ["product"]),
    isOpen: {
      get() {
        return this.open;
      },
      set(value) {
        return this.$emit("update:open", value);
      },
    },
    ...mapGetters("app", ["currencyCode"]),
    fullTelephoneNumber() {
      return this.selectedDialCode + this.telephone?.toString();
    },
    telephoneCodesOrdered() {
      const countryCodesToMoveToTop = ["GB", "US", "AU"];
      const codes = cloneDeep(telephoneCodes);

      return codes
        .sort((a, b) => a["dial_code"] - b["dial_code"])
        .sort((a, b) => {
          return countryCodesToMoveToTop.includes(a.code)
            ? -1
            : countryCodesToMoveToTop.includes(b.code)
            ? 1
            : 0;
        });
    },
    isSMSStockNotificationEnabled() {
      return (
        regionFeatureToggles[this.currencyCode].smsStockNotificationsEnabled ||
        false
      );
    },
  },
  watch: {
    showPhoneNumberInput(val) {
      val && this.preSelectUsersTelephoneCode();
    },
  },
  methods: {
    handleCodeChange(event) {
      const value = event.target.value;
      this.usersCountryCode = value;

      const { dial_code } = this.codes.find((code) => {
        return code.code === value;
      });

      this.selectedDialCode = dial_code;
    },
    async preSelectUsersTelephoneCode() {
      const response = await fetch(
        "https://pro.ip-api.com/json?key=Y6SLxPFTt7gdeOe"
      );
      const { countryCode: usersCountryCode } = await response.json();

      const { dial_code } = this.codes.find(
        (code) => code.code === usersCountryCode
      );

      this.selectedDialCode = dial_code;
      this.usersCountryCode = usersCountryCode;
    },
    async handleSMSNotification() {
      const updateProfileResponse = await fetch(
        `${process.env.RATANDBOA_NODE_APP_URL}/klaviyo/sms-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: this.email,
            phone_number: this.fullTelephoneNumber,
          }),
        }
      );

      if (!updateProfileResponse.ok) {
        this.success = false;
        return false;
      }

      smsCaptureEvent({ location: "back in stock" });

      return true;
    },
    async handleMarketingSignUp() {
      const currencycCode = this.currencyCode.toLowerCase();
      const klaviyo = klaviyoLists[currencycCode];

      const payload = {
        data: {
          type: "subscription",
          attributes: {
            custom_source: "bis_signup",
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: this.email,
                  ...(this.fullTelephoneNumber && {
                    phone_number: this.fullTelephoneNumber,
                  }),
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: "list",
                id: klaviyo.listId,
              },
            },
          },
        },
      };

      const requestOptions = {
        method: "POST",
        headers: {
          revision: "2024-05-15",
          "content-type": "application/json",
        },
        body: JSON.stringify(payload),
      };

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${klaviyo.accountId}`,
        requestOptions
      );

      if (response.status === 202) {
        return true;
      }

      return false;
    },
    async handleFormSubmit() {
      const currencycCode = this.currencyCode.toLowerCase();
      const klaviyo = klaviyoLists[currencycCode];

      this.v$.email.$touch();
      if (this.v$.email.$invalid) {
        return;
      }

      if (this.showPhoneNumberInput) {
        this.v$.telephone.$touch();
        this.v$.selectedDialCode.$touch();

        if (this.v$.telephone.$invalid || this.v$.selectedDialCode.$invalid) {
          return;
        }
      }

      const variantId = this.variant.id.split("ProductVariant/")[1];

      const payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: this.email,
                  ...(this.fullTelephoneNumber && {
                    phone_number: this.fullTelephoneNumber,
                  }),
                },
              },
            },
            channels: ["EMAIL", ...(this.fullTelephoneNumber ? ["SMS"] : [])],
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-05-15",
        },
        body: JSON.stringify(payload),
      };

      const bisResponse = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${klaviyo.accountId}`,
        requestOptions
      );

      if (bisResponse.status === 202) {
        this.success = true;

        if (this.marketing) {
          const status = await this.handleMarketingSignUp();
          this.status = status;
        }

        if (this.showPhoneNumberInput) {
          const status = await this.handleSMSNotification();
          this.success = status;
        }

        this.submitted = true;

        bisEvent();

        setTimeout(() => {
          this.isOpen = false;
        }, 3000);
      } else {
        this.success = false;
        this.submitted = true;
      }
    },
  },
  validations() {
    return {
      email: { required, email },
      telephone: { required, numeric },
      selectedDialCode: { required },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";
.legal {
  font-size: 11px;
  margin: 0 0 30px 0;
}
</style>
